<template>
  <b-card class="rounded-lg">
    <report-header
      :title="$t('ledger_rep_contract_status')"
      :scope-label="scopeLabel"
      :can-company="canCompany"
      :company="company"
      @company="company = $event; load()"
      @print="printReport"
    />
    <div class="d-flex flex-wrap mb-1">
      <b-form-input
        v-model="asOf"
        type="date"
        class="mr-50 mb-50 ledger-report-date"
      />
      <b-form-select
        v-model="status"
        :options="statusOptions"
        class="mr-50 mb-50 ledger-report-date"
      />
      <b-button
        size="sm"
        variant="primary"
        @click="load"
      >
        {{ $t('ledger_assets_load') }}
      </b-button>
    </div>
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner />
    </div>
    <template v-else-if="report">
      <div class="d-none d-md-block">
        <b-table
          :items="report.contracts || []"
          :fields="fields"
          small
          responsive
          class="ledger-dense-table"
        >
          <template #cell(contract_number)="data">
            <b-link
              v-if="hasPermission('view contracts')"
              :to="{ name: 'ledger-contracts-view', params: { id: data.item.contract_id } }"
            >
              {{ data.item.contract_number }}
            </b-link>
            <span v-else>{{ data.item.contract_number }}</span>
          </template>
          <template #cell(percent_complete)="data">
            {{ percentLabel(data.item.percent_complete) }}
          </template>
          <template #cell(contract_value)="data">
            <span
              class="ledger-num"
              :class="{ 'text-danger': data.item.is_loss }"
            >{{ money(data.item.contract_value) }}</span>
          </template>
          <template #cell(billed_to_date)="data"><span class="ledger-num">{{ money(data.item.billed_to_date) }}</span></template>
          <template #cell(revenue_recognized)="data"><span class="ledger-num">{{ money(data.item.revenue_recognized) }}</span></template>
          <template #cell(retention_held)="data"><span class="ledger-num">{{ money(data.item.retention_held) }}</span></template>
          <template #cell(status)="data">
            {{ enumLabel(data.item.status, $i18n.locale) }}
            <b-badge
              v-if="data.item.ended"
              variant="dark"
              class="ml-50"
            >
              {{ $t('ledger_ct_ended') }}
            </b-badge>
            <b-badge
              v-if="Number(data.item.unearned_revenue) > 0"
              variant="warning"
              class="ml-50"
            >
              {{ $t('ledger_ct_billed_ahead') }} {{ money(data.item.unearned_revenue) }}
            </b-badge>
            <b-badge
              v-if="Number(data.item.unbilled_revenue) > 0"
              variant="info"
              class="ml-50"
            >
              {{ $t('ledger_ct_unbilled') }} {{ money(data.item.unbilled_revenue) }}
            </b-badge>
            <b-badge
              v-if="data.item.is_loss"
              variant="danger"
              class="ml-50"
            >
              {{ $t('ledger_ct_loss') }}
            </b-badge>
          </template>
        </b-table>
      </div>
      <div class="d-md-none">
        <article
          v-for="row in report.contracts || []"
          :key="row.contract_id"
          class="ledger-report-card"
          :class="{ 'text-danger': row.is_loss }"
        >
          <div>{{ row.contract_number }}</div>
          <div class="ledger-num">
            {{ money(row.contract_value) }}
          </div>
          <b-badge
            v-if="row.ended"
            variant="dark"
          >
            {{ $t('ledger_ct_ended') }}
          </b-badge>
        </article>
      </div>
      <div
        v-if="report.totals"
        class="ledger-num mt-1"
      >
        {{ $t('ledger_rep_loss_count') }} {{ report.totals.loss_contracts }}
      </div>
    </template>
  </b-card>
</template>

<script>
import {
  BCard, BFormInput, BFormSelect, BButton, BSpinner, BTable, BLink, BBadge,
} from 'bootstrap-vue'
import { getContractStatus } from '@/api/accounting/financialReports'
import { oneFrom, enumLabel } from '@/utils/ledger'
import ReportHeader from './ReportHeader.vue'
import reportMixin from './reportMixin'

export default {
  name: 'ContractStatusReport',
  components: {
    BCard, BFormInput, BFormSelect, BButton, BSpinner, BTable, BLink, BBadge, ReportHeader,
  },
  mixins: [reportMixin],
  data() {
    return { status: '' }
  },
  computed: {
    statusOptions() {
      return [
        { value: '', text: this.$t('ledger_assets_all_statuses') },
        { value: 'active', text: this.$t('ledger_ct_status_active') },
        { value: 'handed_over', text: this.$t('ledger_ct_status_handed') },
        { value: 'closed', text: this.$t('ledger_ct_status_closed') },
      ]
    },
    fields() {
      return [
        { key: 'contract_number', label: this.$t('ledger_ct_number') },
        { key: 'project_name', label: this.$t('ledger_payroll_project') },
        { key: 'customer_name', label: this.$t('ledger_inv_customer') },
        { key: 'contract_value', label: this.$t('ledger_ct_value') },
        { key: 'percent_complete', label: this.$t('ledger_ct_percent') },
        { key: 'billed_to_date', label: this.$t('ledger_ct_billed') },
        { key: 'revenue_recognized', label: this.$t('ledger_ct_recognized') },
        { key: 'retention_held', label: this.$t('ledger_ct_retention_held') },
        { key: 'status', label: this.$t('ledger_status') },
      ]
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    percentLabel(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${(n * 100).toFixed(2)}%`
    },
    async load() {
      this.loading = true
      try {
        const params = { as_of: this.asOf, ...this.scopeParams }
        if (this.status) params.status = this.status
        const res = await getContractStatus(params)
        this.report = oneFrom(res)
      } catch (error) {
        this.toastError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-num { font-variant-numeric: tabular-nums; }
.ledger-report-date { max-width: 11rem; }
.ledger-report-card { border-bottom: 1px solid #eee; padding: 0.75rem 0; }
</style>
